import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, MapPin, Clock, Phone } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToId } from "../lib/scroll";
import { BUSINESS, IMAGES } from "../constants/business";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden noise" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <img
          src={IMAGES.hero}
          alt="Chevrolet pickup on a lift inside a professional auto repair shop"
          className="w-full h-[120%] object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/40" />

      <div className="relative z-10 px-6 md:px-12 lg:px-24 pb-24 md:pb-32 pt-40 w-full">
        <motion.img
          src="/logo.png"
          alt="Ascencio Auto Repair — official logo"
          data-testid="hero-logo"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="h-16 md:h-20 w-auto mb-10"
          draggable="false"
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-2 text-[#FF3B30] text-xs md:text-sm uppercase tracking-[0.35em] font-semibold mb-6"
          data-testid="hero-eyebrow"
        >
          <MapPin className="w-4 h-4" strokeWidth={2} /> Oklahoma City, OK
        </motion.p>

        <h1 className="font-oswald font-bold uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-8xl" data-testid="hero-heading">
          <MaskedLine delay={0.35}>Diesel &amp; Auto</MaskedLine>
          <MaskedLine delay={0.5}>Repair Done</MaskedLine>
          <MaskedLine delay={0.65} className="text-[#F5C518]">Right.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="mt-6 max-w-xl text-zinc-300 text-base md:text-lg"
          data-testid="hero-subtext"
        >
          Cummins · Duramax · Power Stroke specialists. Advanced computer diagnostics
          for cars, SUVs and pickups — gas and diesel. {BUSINESS.tagline}.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => scrollToId("request")}
            data-testid="hero-request-service-btn"
            className="bg-[#FF3B30] text-white font-oswald uppercase tracking-[0.15em] text-base px-10 py-4 hover:bg-white hover:text-black transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
          >
            Request Service
          </button>
          <button
            onClick={() => scrollToId("services")}
            data-testid="hero-view-services-btn"
            className="border border-zinc-500 text-white font-oswald uppercase tracking-[0.15em] text-base px-10 py-4 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-[#FF3B30] focus-visible:outline-none"
          >
            View Services
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-2 text-xs uppercase tracking-[0.2em] text-zinc-400"
        >
          <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#FF3B30]" /> {BUSINESS.hoursDays} · {BUSINESS.hoursTime}</span>
          <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#FF3B30]" /> {BUSINESS.street}</span>
          <a href={BUSINESS.phoneHref} className="flex items-center gap-2 hover:text-[#F5C518] transition-colors duration-200" data-testid="hero-phone-link">
            <Phone className="w-3.5 h-3.5 text-[#FF3B30]" /> {BUSINESS.phone}
          </a>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-6 right-6 md:right-12 z-10 text-zinc-500"
        animate={reduce ? {} : { y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
      >
        <ArrowDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
};
